import {
  TWO_PI,
  STRAND_CONFIGS,
  FRAME_FRACTION,
  COMPACT_FRAME_FRACTION,
  BASE_WIDTH_FRACTION,
  STRAND_ALPHA,
  TRAVEL_SPEED,
  SEGMENTS,
  BLUR_FRACTION,
  CORNER_RADIUS_FRACTION,
} from './ai-glow-border.types';

interface Point {
  x: number;
  y: number;
}

/**
 * Point on a rounded-rectangle perimeter at parameter t (0..1),
 * offset by inset pixels. Corners follow circular arcs.
 * 1:1 port from Android perimeterPoint().
 */
function perimeterPoint(
  t: number,
  width: number,
  height: number,
  inset: number,
  cornerRadius: number
): Point {
  const r = Math.max(0, Math.min(cornerRadius, Math.min(width, height) / 2));

  const straightH = Math.max(0, width - 2 * r);
  const straightV = Math.max(0, height - 2 * r);
  const arcLen = (r * Math.PI) / 2;
  const totalLen = 2 * straightH + 2 * straightV + 4 * arcLen;
  if (totalLen < 0.001) return { x: width / 2, y: height / 2 };

  let pos = (((t % 1) + 1) % 1) * totalLen;
  const er = Math.max(0.01, r - inset);

  // Top edge (L->R)
  if (pos <= straightH) return { x: r + pos, y: inset };
  pos -= straightH;

  // Top-right arc
  if (pos <= arcLen) {
    const a = -Math.PI / 2 + (arcLen > 0 ? pos / arcLen : 0) * (Math.PI / 2);
    return { x: width - r + er * Math.cos(a), y: r + er * Math.sin(a) };
  }
  pos -= arcLen;

  // Right edge (T->B)
  if (pos <= straightV) return { x: width - inset, y: r + pos };
  pos -= straightV;

  // Bottom-right arc
  if (pos <= arcLen) {
    const a = (arcLen > 0 ? pos / arcLen : 0) * (Math.PI / 2);
    return {
      x: width - r + er * Math.cos(a),
      y: height - r + er * Math.sin(a),
    };
  }
  pos -= arcLen;

  // Bottom edge (R->L)
  if (pos <= straightH) return { x: width - r - pos, y: height - inset };
  pos -= straightH;

  // Bottom-left arc
  if (pos <= arcLen) {
    const a = Math.PI / 2 + (arcLen > 0 ? pos / arcLen : 0) * (Math.PI / 2);
    return { x: r + er * Math.cos(a), y: height - r + er * Math.sin(a) };
  }
  pos -= arcLen;

  // Left edge (B->T)
  if (pos <= straightV) return { x: inset, y: height - r - pos };
  pos -= straightV;

  // Top-left arc
  const frac = arcLen > 0 ? Math.max(0, Math.min(1, pos / arcLen)) : 0;
  const a = Math.PI + frac * (Math.PI / 2);
  return { x: r + er * Math.cos(a), y: r + er * Math.sin(a) };
}

/**
 * Smooth open curve through points using Catmull-Rom -> cubic Bezier.
 * Endpoints are clamped for natural strand termination.
 * 1:1 port from Android smoothOpenPath().
 */
function smoothOpenPath(ctx: CanvasRenderingContext2D, points: Point[]): void {
  const n = points.length;
  if (n < 2) return;

  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);

  if (n === 2) {
    ctx.lineTo(points[1]!.x, points[1]!.y);
    return;
  }

  const tension = 1 / 3;
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)]!;
    const p1 = points[i]!;
    const p2 = points[Math.min(i + 1, n - 1)]!;
    const p3 = points[Math.min(i + 2, n - 1)]!;
    ctx.bezierCurveTo(
      p1.x + (p2.x - p0.x) * tension,
      p1.y + (p2.y - p0.y) * tension,
      p2.x - (p3.x - p1.x) * tension,
      p2.y - (p3.y - p1.y) * tension,
      p2.x,
      p2.y
    );
  }
}

/**
 * Parse hex color to "r, g, b" string for use with rgba().
 */
function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

// Pre-compute RGB values for all strand colors
const strandRgbCache: string[] = STRAND_CONFIGS.map((cfg) =>
  hexToRgb(cfg.color)
);

/**
 * Main draw function. Called every rAF frame.
 * 1:1 port of Android's drawWithContent { ... } block.
 */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  animationPhase: number,
  opacityPulse: number,
  compact: boolean
): void {
  ctx.clearRect(0, 0, w, h);

  const frameFraction = compact ? COMPACT_FRAME_FRACTION : FRAME_FRACTION;
  const frameThickness = Math.min(w * frameFraction, h / 2);
  const baseInset = 0;
  const amplitude = frameThickness * 0.34;
  const baseStrokeWidth = frameThickness * BASE_WIDTH_FRACTION;
  const blurPx = Math.max(1, frameThickness * BLUR_FRACTION);
  const cRadius = frameThickness * CORNER_RADIUS_FRACTION;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalCompositeOperation = 'source-over';

  for (let si = 0; si < STRAND_CONFIGS.length; si++) {
    const cfg = STRAND_CONFIGS[si]!;
    const rgb = strandRgbCache[si]!;
    const effectivePhase = animationPhase * cfg.speedMultiplier;
    const arcStart =
      (cfg.startOffset +
        (animationPhase / TWO_PI) * TRAVEL_SPEED * cfg.speedMultiplier) %
      1;

    // Generate oscillating anchor points along the arc
    const points: Point[] = new Array(cfg.pointCount);
    const insets: number[] = new Array(cfg.pointCount);

    for (let i = 0; i < cfg.pointCount; i++) {
      const localT = i / (cfg.pointCount - 1);
      const globalT = (arcStart + localT * cfg.arcLength) % 1;
      const angle =
        localT * cfg.waveFrequency * TWO_PI + cfg.phaseOffset + effectivePhase;
      const oscillation = Math.sin(angle) * amplitude * cfg.amplitudeMultiplier;
      const inset = baseInset + oscillation;
      points[i] = perimeterPoint(globalT, w, h, inset, cRadius);
      insets[i] = inset;
    }

    const strandWidth = baseStrokeWidth * cfg.widthMultiplier;

    // Glow pass: wide soft halo
    ctx.shadowColor = cfg.color;
    ctx.shadowBlur = blurPx;
    ctx.strokeStyle = `rgba(${rgb}, ${0.28 * opacityPulse})`;
    ctx.lineWidth = strandWidth * 3.5;
    smoothOpenPath(ctx, points);
    ctx.stroke();

    // Reset shadow for main pass
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // Main pass: segmented for variable thickness + edge fade
    for (let seg = 0; seg < SEGMENTS; seg++) {
      const rawStart = ((seg * cfg.pointCount) / SEGMENTS) | 0;
      const rawEnd = (((seg + 1) * cfg.pointCount) / SEGMENTS) | 0;
      const startIdx = Math.max(0, rawStart - 1);
      const endIdx = Math.min(cfg.pointCount, rawEnd + 1);
      if (endIdx - startIdx < 3) continue;

      const segPoints = points.slice(startIdx, endIdx);

      // Steep bell-curve taper (squared sin): nearly zero at ends
      const segNorm = (seg + 0.5) / SEGMENTS;
      const taper = Math.sin(segNorm * Math.PI);
      const taperSteep = taper * taper;
      const segWidth = strandWidth * (0.05 + 0.95 * taperSteep);

      // Edge proximity fade
      let insetSum = 0;
      for (let idx = startIdx; idx < endIdx; idx++) {
        insetSum += insets[idx]!;
      }
      const avgInset = insetSum / (endIdx - startIdx);
      const edgeFade = Math.max(
        0.15,
        Math.min(1, 1 - avgInset / (frameThickness * 1.2))
      );

      const segAlpha =
        STRAND_ALPHA * opacityPulse * Math.max(0.05, taperSteep) * edgeFade;

      ctx.strokeStyle = `rgba(${rgb}, ${segAlpha})`;
      ctx.lineWidth = segWidth;
      smoothOpenPath(ctx, segPoints);
      ctx.stroke();
    }
  }
}
